import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Evento } from '../../models/evento-model';

declare global {
  interface Window {
    dataLayer?: Array<AnalyticsEventParams & { event?: string } | IArguments | unknown[]>;
    gtag?: (...args: unknown[]) => void;
  }
}

export interface AnalyticsEventParams {
  [key: string]: string | number | boolean | null | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly storageKey = 'tc_eventos_utm';
  private readonly measurementId = environment.ga4MeasurementId ?? '';
  private initialized = false;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.captureUtmFromUrl();
  }

  initialize(): void {
    if (!this.isBrowser() || !this.measurementId) {
      return;
    }

    this.ensureInitialized();
  }

  trackEvent(eventName: string, params: AnalyticsEventParams = {}): void {
    if (!this.isBrowser()) {
      return;
    }

    const enrichedParams = {
      ...this.getStoredUtmParams(),
      ...this.cleanParams(params),
      ...this.getDebugParams()
    };

    this.pushDataLayerEvent(eventName, enrichedParams);

    if (!this.measurementId) {
      return;
    }

    this.ensureInitialized();
    window.gtag?.('event', eventName, enrichedParams);
  }

  trackEventosHomeView(): void {
    this.trackEvent('events_home_view');
  }

  trackEventCardClick(evento: Evento, action: 'view_details' | 'register'): void {
    this.trackEvent('event_card_click', {
      ...this.getEventoParams(evento),
      action
    });
  }

  trackEventView(evento: Evento): void {
    this.trackEvent('event_view', this.getEventoParams(evento));
  }

  trackEventAction(evento: Evento, action: 'registration_start_click' | 'participants_list_click' | 'ranking_click' | 'map_click'): void {
    this.trackEvent(action, this.getEventoParams(evento));
  }

  trackParticipantsListView(evento: Evento, listaLiberada: boolean, totalInscritosVisivel: number): void {
    this.trackEvent('participants_list_view', {
      ...this.getEventoParams(evento),
      lista_liberada: listaLiberada,
      total_inscritos_visivel: totalInscritosVisivel
    });
  }

  trackRegistrationStart(evento: Evento, registrationType: string): void {
    this.trackEvent('registration_start', {
      ...this.getEventoParams(evento),
      registration_type: registrationType
    });
  }

  trackRegistrationStep(evento: Evento, stepName: string, registrationType: string): void {
    this.trackEvent('registration_step', {
      ...this.getEventoParams(evento),
      registration_step: stepName,
      registration_type: registrationType
    });
  }

  trackRegistrationSubmit(evento: Evento, registrationType: string, paymentMethod?: string): void {
    this.trackEvent('registration_submit', {
      ...this.getEventoParams(evento),
      registration_type: registrationType,
      payment_method: paymentMethod
    });
  }

  trackRegistrationSuccess(evento: Evento, registrationType: string, paymentMethod?: string): void {
    this.trackEvent('registration_success', {
      ...this.getEventoParams(evento),
      registration_type: registrationType,
      payment_method: paymentMethod
    });
  }

  trackRegistrationError(evento: Evento, registrationType: string, errorMessage?: string): void {
    this.trackEvent('registration_error', {
      ...this.getEventoParams(evento),
      registration_type: registrationType,
      error_message: this.normalizeValue(errorMessage)
    });
  }

  private ensureInitialized(): void {
    if (this.initialized || !this.measurementId) {
      return;
    }

    window.dataLayer = window.dataLayer ?? [];
    window.gtag = window.gtag ?? function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      send_page_view: false,
      ...this.getDebugParams()
    });
    this.initialized = true;
  }

  private pushDataLayerEvent(eventName: string, params: AnalyticsEventParams): void {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
      event: eventName,
      event_name: eventName,
      ...params
    });
  }

  private captureUtmFromUrl(): void {
    if (!this.isBrowser()) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const utmParams = {
      utm_source: searchParams.get('utm_source') ?? undefined,
      utm_medium: searchParams.get('utm_medium') ?? undefined,
      utm_campaign: searchParams.get('utm_campaign') ?? undefined,
      utm_content: searchParams.get('utm_content') ?? undefined
    };

    if (Object.values(utmParams).some(Boolean)) {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.cleanParams(utmParams)));
    }
  }

  private getStoredUtmParams(): AnalyticsEventParams {
    if (!this.isBrowser()) {
      return {};
    }

    try {
      return JSON.parse(sessionStorage.getItem(this.storageKey) ?? '{}');
    } catch {
      return {};
    }
  }

  private getEventoParams(evento: Evento): AnalyticsEventParams {
    return {
      id_evento: evento.id,
      titulo_evento: this.normalizeValue(evento.titulo),
      status_inscricao: this.getStatusInscricao(evento),
      tipo_evento: evento.tipoEvento
    };
  }

  private getStatusInscricao(evento: Evento): string {
    if (evento.isInscricaoAberta()) {
      return 'aberta';
    }

    if (evento.isInscricaoEmBreve()) {
      return 'em_breve';
    }

    if (evento.isInscricaoEncerrada()) {
      return 'encerrada';
    }

    return 'nao_informado';
  }

  private cleanParams(params: AnalyticsEventParams): AnalyticsEventParams {
    return Object.entries(params).reduce<AnalyticsEventParams>((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }

      return acc;
    }, {});
  }

  private normalizeValue(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }

    return value.slice(0, 100);
  }

  private getDebugParams(): AnalyticsEventParams {
    if (!this.isBrowser() || window.location.hostname !== 'localhost') {
      return {};
    }

    return { debug_mode: true };
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
